<script lang="ts" setup>
import { ref } from 'vue';
import PageHeader from '@/components/PageHeader.vue';
import DataView from '@/components/DataView.vue';

const activeTab = ref('list');
const showDeleteModal = ref(false);

// Detail View tab state
const detailTab = ref('overview');

// Settings Page tab state
const isEditing = ref(false);
const showFormDeleteModal = ref(false);

// ── List/Table tab ──────────────────────────────────────────────────────────
const selectedWorkflowName = ref<string>('Daily Data Sync');

const showcaseWorkflows = [
  { name: 'Daily Data Sync',      status: 'Active',     statusClass: 'bg-success',           lastRun: '2 hours ago',    executions: '1,234' },
  { name: 'Invoice Processing',   status: 'Draft',      statusClass: 'bg-warning',  lastRun: 'Never',          executions: '0'     },
  { name: 'Email Notifications',  status: 'Error',      statusClass: 'bg-danger',             lastRun: '1 day ago',      executions: '89'    },
  { name: 'Monthly Reports',      status: 'Processing', statusClass: 'bg-info',               lastRun: '5 minutes ago',  executions: '456'   },
  { name: 'Backup Service',       status: 'Archived',   statusClass: 'bg-secondary',          lastRun: '30 days ago',    executions: '2,890' },
];

const showcaseTableOptions = {
  select: true,
  columns: [
    { data: 'name',       title: 'Workflow Name' },
    { data: 'status',     title: 'Status',
      render: (_: string, __: string, row: any) => `<span class="badge ${row.statusClass}">${row.status}</span>` },
    { data: 'lastRun',    title: 'Last Run',   className: 'text-muted' },
    { data: 'executions', title: 'Executions',
      render: (data: string) => `<span class="badge bg-primary">${data}</span>` },
  ],
  layout: { topEnd: null, bottomEnd: null, bottomStart: null }
};

function handleShowcaseRowClick(row: any, _index: number, event: Event) {
  const rowEl = (event.target as HTMLElement).closest('tbody tr') as HTMLTableRowElement | null;
  if (rowEl) {
    rowEl.closest('tbody')?.querySelectorAll('tr').forEach(r => r.classList.remove('table-active'));
    rowEl.classList.add('table-active');
    selectedWorkflowName.value = row.name;
  }
}
</script>

<template>
  <!-- =====================================================================
       SUB-TAB NAVIGATION
       ===================================================================== -->
  <ul class="nav nav-tabs mb-4">
    <li class="nav-item">
      <button
        class="nav-link"
        :class="{ active: activeTab === 'list' }"
        @click="activeTab = 'list'"
      >
        List/Table
      </button>
    </li>
    <li class="nav-item">
      <button
        class="nav-link"
        :class="{ active: activeTab === 'grid' }"
        @click="activeTab = 'grid'"
      >
        Card Grid
      </button>
    </li>
    <li class="nav-item">
      <button
        class="nav-link"
        :class="{ active: activeTab === 'form' }"
        @click="activeTab = 'form'"
      >
        Form
      </button>
    </li>
    <li class="nav-item">
      <button
        class="nav-link"
        :class="{ active: activeTab === 'modal' }"
        @click="activeTab = 'modal'"
      >
        Delete Modal
      </button>
    </li>
    <li class="nav-item">
      <button
        class="nav-link"
        :class="{ active: activeTab === 'detail' }"
        @click="activeTab = 'detail'"
      >
        Detail View
      </button>
    </li>

    <li class="nav-item">
      <button
        class="nav-link"
        :class="{ active: activeTab === 'settings' }"
        @click="activeTab = 'settings'"
      >
        Settings Page
      </button>
    </li>
  </ul>

  <!-- =====================================================================
       LIST/TABLE TAB
       ===================================================================== -->
  <div v-if="activeTab === 'list'">
    <h3 class="mb-3">Table with DataView</h3>
    <p class="text-muted mb-4">
      Shows a workflow list using the <code>DataView</code> component with sortable columns
      and selectable rows. Click any row to select it.
      Currently selected: <strong>{{ selectedWorkflowName }}</strong>
    </p>

    <DataView
      :data="showcaseWorkflows"
      :view-mode="'table'"
      :options="showcaseTableOptions"
      @row-click="handleShowcaseRowClick"
    />

    <div class="principles-list mt-4">
      <h4 class="mb-3">Design Principles</h4>
      <ul class="small text-muted mb-0">
        <li class="mb-2"><strong>Always use DataView</strong> - never write a raw <code>&lt;table&gt;</code>; DataView adds sorting, pagination, and responsive behaviour for free</li>
        <li class="mb-2"><strong>viewMode="table"</strong> - forces table layout; omit or use <code>"grid"</code> for card grid mode</li>
        <li class="mb-2"><strong>Selectable rows</strong> - track the selection in a <code>ref</code>, apply <code>table-active</code> via <code>rowCallback</code> and keep it in sync on <code>@row-click</code></li>
        <li class="mb-2"><strong>Status badges</strong> - Green=Active, Orange=Draft (<code>text-dark</code>), Red=Error, Blue=Processing, Gray=Archived</li>
        <li><strong>Destructive actions</strong> - use <code>btn-outline-danger</code> in tables, never a solid danger button</li>
      </ul>
    </div>
  </div>

  <!-- =====================================================================
       CARD GRID TAB
       ===================================================================== -->
  <div v-if="activeTab === 'grid'">
    <h3 class="mb-3">Card Grid with Actions</h3>
    <p class="text-muted mb-4">
      Shows project cards in a responsive grid with primary action and overflow menu.
    </p>

    <div class="row g-3">
      <div class="col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <h5 class="card-title mb-0">Project Alpha</h5>
              <span class="badge bg-success">Active</span>
            </div>
            <p class="card-text text-muted small mb-2">
              5 workflows
            </p>
            <p class="card-text text-muted small mb-0">
              Last run: 2 hours ago
            </p>
          </div>
          <div class="card-footer bg-transparent">
            <div class="d-flex justify-content-end gap-2">
              <button class="bb-btn-primary-cta btn-sm flex-grow-1">
                <i class="bi bi-folder-open me-1"></i>Open
              </button>
              <div class="dropdown">
                <button
                  class="bb-btn-secondary-cta btn-sm dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i class="bi bi-three-dots"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><a class="dropdown-item" href="#"><i class="bi bi-pencil me-2"></i>Edit</a></li>
                  <li><a class="dropdown-item" href="#"><i class="bi bi-download me-2"></i>Export</a></li>
                  <li><a class="dropdown-item" href="#"><i class="bi bi-files me-2"></i>Duplicate</a></li>
                  <li><hr class="dropdown-divider"></li>
                  <li><a class="dropdown-item text-danger" href="#"><i class="bi bi-trash me-2"></i>Delete</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <h5 class="card-title mb-0">Project Beta</h5>
              <span class="badge bg-warning">Draft</span>
            </div>
            <p class="card-text text-muted small mb-2">
              2 workflows
            </p>
            <p class="card-text text-muted small mb-0">
              Last run: Never
            </p>
          </div>
          <div class="card-footer bg-transparent">
            <div class="d-flex justify-content-end gap-2">
              <button class="bb-btn-primary-cta btn-sm flex-grow-1">
                <i class="bi bi-folder-open me-1"></i>Open
              </button>
              <div class="dropdown">
                <button
                  class="bb-btn-secondary-cta btn-sm dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i class="bi bi-three-dots"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><a class="dropdown-item" href="#"><i class="bi bi-pencil me-2"></i>Edit</a></li>
                  <li><a class="dropdown-item" href="#"><i class="bi bi-download me-2"></i>Export</a></li>
                  <li><a class="dropdown-item" href="#"><i class="bi bi-files me-2"></i>Duplicate</a></li>
                  <li><hr class="dropdown-divider"></li>
                  <li><a class="dropdown-item text-danger" href="#"><i class="bi bi-trash me-2"></i>Delete</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <h5 class="card-title mb-0">Project Gamma</h5>
              <span class="badge bg-danger">Error</span>
            </div>
            <p class="card-text text-muted small mb-2">
              8 workflows
            </p>
            <p class="card-text text-muted small mb-0">
              Last run: 1 day ago
            </p>
          </div>
          <div class="card-footer bg-transparent">
            <div class="d-flex justify-content-end gap-2">
              <button class="bb-btn-primary-cta btn-sm flex-grow-1">
                <i class="bi bi-folder-open me-1"></i>Open
              </button>
              <div class="dropdown">
                <button
                  class="bb-btn-secondary-cta btn-sm dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i class="bi bi-three-dots"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><a class="dropdown-item" href="#"><i class="bi bi-pencil me-2"></i>Edit</a></li>
                  <li><a class="dropdown-item" href="#"><i class="bi bi-download me-2"></i>Export</a></li>
                  <li><a class="dropdown-item" href="#"><i class="bi bi-files me-2"></i>Duplicate</a></li>
                  <li><hr class="dropdown-divider"></li>
                  <li><a class="dropdown-item text-danger" href="#"><i class="bi bi-trash me-2"></i>Delete</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-lg-4">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <h5 class="card-title mb-0 text-muted">Legacy Project</h5>
              <span class="badge bg-secondary">Archived</span>
            </div>
            <p class="card-text text-muted small mb-2">
              12 workflows
            </p>
            <p class="card-text text-muted small mb-0">
              Last run: 30 days ago
            </p>
          </div>
          <div class="card-footer bg-transparent">
            <div class="d-flex justify-content-end gap-2">
              <button class="bb-btn-primary-cta btn-sm flex-grow-1">
                <i class="bi bi-folder-open me-1"></i>Open
              </button>
              <div class="dropdown">
                <button
                  class="bb-btn-secondary-cta btn-sm dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i class="bi bi-three-dots"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li><a class="dropdown-item" href="#"><i class="bi bi-pencil me-2"></i>Edit</a></li>
                  <li><a class="dropdown-item" href="#"><i class="bi bi-download me-2"></i>Export</a></li>
                  <li><a class="dropdown-item" href="#"><i class="bi bi-files me-2"></i>Duplicate</a></li>
                  <li><hr class="dropdown-divider"></li>
                  <li><a class="dropdown-item text-danger" href="#"><i class="bi bi-trash me-2"></i>Delete</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="principles-list mt-4">
      <h4 class="mb-3">Design Principles</h4>
      <ul class="small text-muted mb-0">
        <li class="mb-2"><strong>Primary action</strong> - Main card action gets <code>btn-primary btn-sm</code>, uses <code>flex-grow-1</code></li>
        <li class="mb-2"><strong>Overflow menu</strong> - Three-dots icon (<code>bi-three-dots</code>) for additional actions</li>
        <li><strong>Destructive in dropdown</strong> - Use <code>text-danger</code> class, place after divider</li>
      </ul>
    </div>
  </div>

  <!-- =====================================================================
       FORM TAB
       ===================================================================== -->
  <div v-if="activeTab === 'form'">
    <h3 class="mb-3">Form with Validation</h3>
    <p class="text-muted mb-4">
      Shows standard form layout with horizontal labels and action buttons.
    </p>

    <form>
      <div class="row mb-3">
        <label for="workflowName" class="col-sm-3 col-form-label">Workflow Name <span class="text-danger">*</span></label>
        <div class="col-sm-9">
          <input type="text" class="form-control" id="workflowName" value="Daily Data Sync" required>
        </div>
      </div>

      <div class="row mb-3">
        <label for="description" class="col-sm-3 col-form-label">Description</label>
        <div class="col-sm-9">
          <textarea class="form-control" id="description" rows="3" placeholder="Enter workflow description">Syncs daily data from external API to internal database</textarea>
        </div>
      </div>

      <div class="row mb-3">
        <label for="status" class="col-sm-3 col-form-label">Status</label>
        <div class="col-sm-9">
          <select class="form-select" id="status">
            <option value="active" selected>Active</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      <div class="row mb-3">
        <label for="schedule" class="col-sm-3 col-form-label">Schedule</label>
        <div class="col-sm-9">
          <input type="text" class="form-control" id="schedule" value="0 0 * * *" placeholder="Cron expression">
          <div class="form-text">Runs daily at midnight (0 0 * * *)</div>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-sm-9 offset-sm-3">
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="enableNotifications" checked>
            <label class="form-check-label" for="enableNotifications">
              Enable email notifications on failure
            </label>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-9 offset-sm-3">
          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="bb-btn-danger btn-sm" @click="showFormDeleteModal = true">
              <i class="bi bi-trash"></i> Delete
            </button>
            <button type="button" class="bb-btn-cancel">Cancel</button>
            <button type="submit" class="bb-btn-primary-cta">Save Changes</button>
          </div>
        </div>
      </div>
    </form>

    <div class="principles-list mt-4">
      <h4 class="mb-3">Design Principles</h4>
      <ul class="small text-muted mb-0">
        <li class="mb-2"><strong>Right-aligned actions</strong> - Use <code>d-flex justify-content-end gap-2</code> for all form action buttons</li>
        <li class="mb-2"><strong>Save Changes rightmost</strong> - Primary action (<code>bb-btn-primary</code>) is always the last button on the right</li>
        <li class="mb-2"><strong>Cancel left of Save</strong> - Use <code>bb-btn-cancel</code> immediately to the left of Save Changes</li>
        <li class="mb-2"><strong>Delete is optional, leftmost</strong> - Use <code>bb-btn-danger btn-sm</code> and always open a confirmation modal — never delete directly</li>
        <li><strong>Required fields</strong> - Mark with red asterisk (<code>&lt;span class="text-danger"&gt;*&lt;/span&gt;</code>)</li>
      </ul>
    </div>
  </div>

  <!-- =====================================================================
       DELETE MODAL TAB
       ===================================================================== -->
  <div v-if="activeTab === 'modal'">
    <h3 class="mb-3">Delete Confirmation Modal</h3>
    <p class="text-muted mb-4">
      Shows the only acceptable use of solid danger button (btn-danger).
    </p>

    <div class="text-center mb-4">
      <button class="bb-btn-secondary-cta" @click="showDeleteModal = true">
        <i class="bi bi-eye me-1"></i>Show Delete Modal
      </button>
    </div>

    <!-- Static Modal Preview -->
    <div class="bd-example">
      <div class="modal-dialog modal-dialog-centered" style="position: relative; z-index: 1;">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title text-danger">
              <i class="bi bi-exclamation-triangle me-2"></i>Delete Workflow
            </h2>
            <button type="button" class="btn-close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete <strong>"Daily Data Sync"</strong>?</p>
            <p class="text-muted mb-0">This action cannot be undone. All execution history will be permanently removed.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="bb-btn-cancel">Cancel</button>
            <button type="button" class="btn btn-danger">Delete</button>
          </div>
        </div>
      </div>
    </div>

    <div class="principles-list mt-4">
      <h4 class="mb-3">Design Principles</h4>
      <ul class="small text-muted mb-0">
        <li class="mb-2"><strong>Solid danger button</strong> - Use <code>btn-danger</code> ONLY in delete confirmation modals</li>
        <li class="mb-2"><strong>Never use solid danger elsewhere</strong> - In tables/lists, always use <code>btn-outline-danger</code></li>
        <li><strong>Modal title</strong> - Use <code>text-danger</code> class and warning icon for destructive actions</li>
      </ul>
    </div>
  </div>

  <!-- =====================================================================
       DETAIL VIEW TAB
       ===================================================================== -->
  <div v-if="activeTab === 'detail'">
    <h3 class="mb-3">Detail View with Tabs</h3>
    <p class="text-muted mb-4">
      Shows a detail page for a single item with tabbed navigation for different sections.
    </p>

    <!-- Detail Page Header -->
    <div class="bd-example">
      <PageHeader
        title="Project Alpha"
        :breadcrumbs="[
          { label: 'Projects', href: '/projects' },
          { label: 'Project Alpha' }
        ]"
      >
        <template #actions>
          <button class="bb-btn-primary-cta">
            Edit Project
          </button>
          <div class="dropdown">
            <button
              class="bb-btn-secondary-cta dropdown-toggle"
              data-bs-toggle="dropdown"
            >
              Actions
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#"><i class="bi bi-download me-2"></i>Export</a></li>
              <li><a class="dropdown-item" href="#"><i class="bi bi-files me-2"></i>Duplicate</a></li>
              <li><hr class="dropdown-divider"></li>
              <li><a class="dropdown-item text-danger" href="#"><i class="bi bi-archive me-2"></i>Archive</a></li>
            </ul>
          </div>
        </template>
      </PageHeader>

      <!-- Detail Tabs -->
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: detailTab === 'overview' }"
            @click="detailTab = 'overview'"
          >
            Overview
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: detailTab === 'workflows' }"
            @click="detailTab = 'workflows'"
          >
            Workflows
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: detailTab === 'history' }"
            @click="detailTab = 'history'"
          >
            History
          </button>
        </li>
      </ul>

      <!-- Tab Content -->
      <div v-if="detailTab === 'overview'" class="p-3 border rounded">
        <h5>Overview</h5>
        <p class="text-muted mb-0">Overview tab content showing project summary, statistics, and key information.</p>
      </div>
      <div v-else-if="detailTab === 'workflows'" class="p-3 border rounded">
        <h5>Workflows</h5>
        <p class="text-muted mb-0">List of workflows within this project with their statuses and actions.</p>
      </div>
      <div v-else-if="detailTab === 'history'" class="p-3 border rounded">
        <h5>History</h5>
        <p class="text-muted mb-0">Execution history and activity log for this project.</p>
      </div>
    </div>

    <div class="principles-list mt-4">
      <h4 class="mb-3">Design Principles</h4>
      <ul class="small text-muted mb-0">
        <li class="mb-2"><strong>One primary action</strong> - "Edit Project" is the main CTA in page header</li>
        <li class="mb-2"><strong>Clean tabs</strong> - No icons in tab labels, keeps interface uncluttered</li>
        <li class="mb-2"><strong>Overflow menu</strong> - Secondary actions grouped in dropdown</li>
        <li><strong>Breadcrumbs</strong> - Clear navigation hierarchy in page header</li>
      </ul>
    </div>
  </div>

  <!-- =====================================================================
       SETTINGS PAGE TAB
       ===================================================================== -->
  <div v-if="activeTab === 'settings'">
    <h3 class="mb-3">Settings/Edit Page</h3>
    <p class="text-muted mb-4">
      Shows a settings page with view/edit mode toggle pattern.
    </p>

    <!-- Settings Page Header -->
    <div class="bd-example">
      <PageHeader
        title="Project Settings"
        :breadcrumbs="[
          { label: 'Projects', href: '/projects' },
          { label: 'Project Alpha', href: '/projects/alpha' },
          { label: 'Settings' }
        ]"
      >
        <template #actions>
          <button
            v-if="!isEditing"
            class="bb-btn-primary-cta"
            @click="isEditing = true"
          >
            Edit Settings
          </button>
          <template v-else>
            <button
              class="bb-btn-primary-cta"
              @click="isEditing = false"
            >
              Save Changes
            </button>
            <button
              class="bb-btn-cancel"
              @click="isEditing = false"
            >
              Cancel
            </button>
          </template>
        </template>
      </PageHeader>

      <!-- Settings Form -->
      <div class="mt-4">
        <!-- Project Name -->
        <div class="row mb-3">
          <label class="col-sm-3 col-form-label">Project Name</label>
          <div class="col-sm-9">
            <input
              v-if="isEditing"
              type="text"
              class="form-control"
              value="Project Alpha"
            />
            <p v-else class="form-control-plaintext">Project Alpha</p>
          </div>
        </div>

        <!-- Description -->
        <div class="row mb-3">
          <label class="col-sm-3 col-form-label">Description</label>
          <div class="col-sm-9">
            <textarea
              v-if="isEditing"
              class="form-control"
              rows="3"
            >A comprehensive workflow automation project for daily operations</textarea>
            <p v-else class="form-control-plaintext">A comprehensive workflow automation project for daily operations</p>
          </div>
        </div>

        <!-- Status -->
        <div class="row mb-3">
          <label class="col-sm-3 col-form-label">Status</label>
          <div class="col-sm-9">
            <select v-if="isEditing" class="form-select">
              <option value="active" selected>Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
            <p v-else class="form-control-plaintext">
              <span class="badge bg-success">Active</span>
            </p>
          </div>
        </div>

        <!-- Enable Notifications -->
        <div class="row mb-3">
          <label class="col-sm-3 col-form-label">Notifications</label>
          <div class="col-sm-9">
            <div v-if="isEditing" class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="enableNotif"
                checked
              />
              <label class="form-check-label" for="enableNotif">
                Enable email notifications
              </label>
            </div>
            <p v-else class="form-control-plaintext">Enabled</p>
          </div>
        </div>
      </div>
    </div>

    <div class="principles-list mt-4">
      <h4 class="mb-3">Design Principles</h4>
      <ul class="small text-muted mb-0">
        <li class="mb-2"><strong>Edit mode toggle</strong> - "Edit" button becomes "Save + Cancel" when editing</li>
        <li class="mb-2"><strong>View mode styling</strong> - Use <code>form-control-plaintext</code> for read-only text display</li>
        <li class="mb-2"><strong>Button order in edit mode</strong> - Save (primary) comes before Cancel (secondary)</li>
        <li><strong>Form layout</strong> - Use Bootstrap horizontal form layout with <code>col-sm-3</code> labels</li>
      </ul>
    </div>
  </div>

  <!-- =========================================================================
       ACTUAL DELETE MODAL (Bootstrap Modal)
       ========================================================================= -->
  <div
    class="modal fade"
    :class="{ show: showDeleteModal }"
    :style="{ display: showDeleteModal ? 'block' : 'none' }"
    tabindex="-1"
    @click.self="showDeleteModal = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>Delete Workflow
          </h2>
          <button type="button" class="btn-close" @click="showDeleteModal = false" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>"Daily Data Sync"</strong>?</p>
          <p class="text-muted mb-0">This action cannot be undone. All execution history will be permanently removed.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="bb-btn-cancel" @click="showDeleteModal = false">Cancel</button>
          <button type="button" class="btn btn-danger" @click="showDeleteModal = false">Delete</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showDeleteModal" class="modal-backdrop fade show" @click="showDeleteModal = false"></div>

  <!-- =========================================================================
       FORM DELETE MODAL
       ========================================================================= -->
  <div
    class="modal fade"
    :class="{ show: showFormDeleteModal }"
    :style="{ display: showFormDeleteModal ? 'block' : 'none' }"
    tabindex="-1"
    @click.self="showFormDeleteModal = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>Delete Workflow
          </h2>
          <button type="button" class="btn-close" @click="showFormDeleteModal = false" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>"Daily Data Sync"</strong>?</p>
          <p class="text-muted mb-0">This action cannot be undone. All execution history will be permanently removed.</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="bb-btn-cancel" @click="showFormDeleteModal = false">Cancel</button>
          <button type="button" class="btn btn-danger" @click="showFormDeleteModal = false">Delete</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showFormDeleteModal" class="modal-backdrop fade show" @click="showFormDeleteModal = false"></div>
</template>

<style scoped lang="scss">
// Bootstrap docs-style example container
.bd-example {
  padding: 1.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  border: 1px solid var(--bs-border-color);
}

// Principles list styling
.principles-list {
  padding: 1rem 1.25rem;

  h5 {
    font-weight: 600;
    color: #212529;
    font-size: 1rem;
  }

  ul {
    padding-left: 1.25rem;
  }

  code {
    font-size: 0.8125rem;
  }
}

// Inline code
code {
  color: #d63384;
  padding: 0.2rem 0.4rem;
  font-size: 0.875em;
}
</style>
